import React, { useState, useMemo, useCallback } from 'react'
import { getAllProblems, PestDisease } from '../data/diseasePestDatabase'
import { allPlants as plants, PlantData as Plant } from '../data/plants-complete'

const DiseasePestGuide: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [selectedPlant, setSelectedPlant] = useState<string>('')
  const [selectedProblem, setSelectedProblem] = useState<PestDisease | null>(null)
  const [filterType, setFilterType] = useState<'all' | 'pest' | 'disease'>('all')
  const [showNatural, setShowNatural] = useState(true)
  const [showChemical, setShowChemical] = useState(true)

  // Get all problems once
  const allProblems = useMemo(() => {
    console.log('Getting all problems:', getAllProblems().length)
    return getAllProblems()
  }, [])

  // Helper to check if a problem affects a specific plant
  const problemAffectsPlant = useCallback((problem: PestDisease, plantId: string): boolean => {
    if (!plantId) return true
    
    const plant = plants.find(p => p.id === plantId)
    if (!plant) {
      console.log('Plant not found:', plantId)
      return false
    }
    
    const lowerPlantId = plant.id.toLowerCase()
    const lowerPlantName = plant.name.toLowerCase()
    
    const affects = problem.affectedPlants.some(affectedId => {
      const lowerAffected = affectedId.toLowerCase()
      
      // Direct match
      if (lowerPlantId === lowerAffected) return true
      // Plant ID contains affected name (e.g., 'tomato-roma' contains 'tomato')
      if (lowerPlantId.includes(lowerAffected)) return true
      // Plant name contains affected name
      if (lowerPlantName.includes(lowerAffected)) return true
      
      return false
    })
    
    return affects
  }, [])

  // Get filtered problems - this must recompute when dependencies change
  const filteredProblems = useMemo(() => {
    console.log('Filtering problems:', { selectedPlant, searchQuery, filterType })
    
    let problems = allProblems
    
    // Filter by plant FIRST
    if (selectedPlant) {
      problems = problems.filter(p => problemAffectsPlant(p, selectedPlant))
      console.log('After plant filter:', problems.length)
    }
    
    // Then filter by search query
    if (searchQuery.trim()) {
      const lowerQuery = searchQuery.toLowerCase()
      problems = problems.filter(problem => 
        problem.name.toLowerCase().includes(lowerQuery) ||
        problem.symptoms.some(s => s.toLowerCase().includes(lowerQuery))
      )
      console.log('After search filter:', problems.length)
    }
    
    // Then filter by type
    if (filterType !== 'all') {
      if (filterType === 'pest') {
        problems = problems.filter(p => p.type === 'pest')
      } else if (filterType === 'disease') {
        problems = problems.filter(p => p.type === 'disease' || p.type === 'fungus' || p.type === 'deficiency')
      }
      console.log('After type filter:', problems.length)
    }
    
    return problems
  }, [allProblems, selectedPlant, searchQuery, filterType, problemAffectsPlant])

  // Helper to find plants affected by a pest/disease
  const getAffectedPlants = useCallback((problem: PestDisease): Plant[] => {
    if (!problem.affectedPlants || problem.affectedPlants.length === 0) return []
    
    const affected: Plant[] = []
    problem.affectedPlants.forEach(plantId => {
      const lowerPlantId = plantId.toLowerCase()
      
      const matches = plants.filter(p => {
        const lowerPId = p.id.toLowerCase()
        const lowerPName = p.name.toLowerCase()
        
        if (lowerPId === lowerPlantId) return true
        if (lowerPId.includes(lowerPlantId)) return true
        if (lowerPName.includes(lowerPlantId)) return true
        
        return false
      })
      
      affected.push(...matches)
    })
    
    const unique = affected.filter((p, i, arr) => arr.findIndex(t => t.id === p.id) === i)
    return unique.slice(0, 10)
  }, [])

  const getSeverityColor = (severity: PestDisease['severity']) => {
    switch (severity) {
      case 'low': return '#4caf50'
      case 'medium': return '#ff9800'
      case 'high': return '#f44336'
      default: return '#757575'
    }
  }

  const getTypeIcon = (type: PestDisease['type']) => {
    switch (type) {
      case 'pest': return '🐛'
      case 'disease': return '🦠'
      case 'fungus': return '🍄'
      case 'deficiency': return '⚠️'
      default: return '❓'
    }
  }

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'vegetable': return '🥬'
      case 'herb': return '🌿'
      case 'flower': return '🌸'
      case 'fruit': return '🍎'
      case 'berry': return '🍇'
      case 'tree': return '🌳'
      case 'shrub': return '🌿'
      case 'nut': return '🥜'
      case 'grain': return '🌾'
      case 'cover_crop': return '🌱'
      case 'green_manure': return '🌾'
      case 'specialty_crop': return '🌽'
      case 'medicinal': return '💊'
      case 'native': return '🌲'
      case 'ornamental': return '🌺'
      case 'mushroom': return '🍄'
      case 'allium': return '🧅'
      case 'cucurbit': return '🎃'
      case 'legume': return '🥜'
      case 'microgreen': return '🌿'
      case 'sprout': return '🌱'
      case 'cover crop': return '🌾'
      default: return '🌱'
    }
  }

  // Group plants by category for dropdown
  const plantsByCategory = useMemo(() => {
    const grouped: Record<string, Plant[]> = {}
    plants.forEach(plant => {
      if (!grouped[plant.category]) {
        grouped[plant.category] = []
      }
      grouped[plant.category].push(plant)
    })
    // Sort each category alphabetically
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => a.name.localeCompare(b.name))
    })
    return grouped
  }, [])

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'vegetable': return 'Vegetables'
      case 'herb': return 'Herbs'
      case 'flower': return 'Flowers'
      case 'fruit': return 'Fruits'
      case 'berry': return 'Berries'
      case 'tree': return 'Trees'
      case 'shrub': return 'Shrubs'
      case 'nut': return 'Nuts'
      case 'grain': return 'Grains'
      case 'cover_crop': return 'Cover Crops'
      case 'green_manure': return 'Green Manure'
      case 'allium': return 'Alliums'
      case 'cucurbit': return 'Squash & Cucurbits'
      case 'legume': return 'Legumes'
      case 'microgreen': return 'Microgreens'
      case 'sprout': return 'Sprouts'
      case 'cover crop': return 'Cover Crops'
      case 'specialty_crop': return 'Specialty Crops'
      case 'medicinal': return 'Medicinal'
      case 'native': return 'Native Plants'
      case 'ornamental': return 'Ornamental'
      case 'mushroom': return 'Mushrooms'
      default: return category.charAt(0).toUpperCase() + category.slice(1).replace('_', ' ')
    }
  }

  // Get counts
  const pestCount = allProblems.filter(p => p.type === 'pest').length
  const diseaseCount = allProblems.filter(p => p.type === 'disease' || p.type === 'fungus' || p.type === 'deficiency').length

  // Handle plant selection change
  const handlePlantChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newPlant = e.target.value
    console.log('Plant selected:', newPlant)
    setSelectedPlant(newPlant)
    setSelectedProblem(null) // Clear any selected problem when changing plant
  }

  // Handle search change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    setSelectedProblem(null)
  }

  // Handle filter type change
  const handleFilterChange = (newFilter: 'all' | 'pest' | 'disease') => {
    console.log('Filter changed to:', newFilter)
    setFilterType(newFilter)
    setSelectedProblem(null)
  }

  return (
    <div>
      {/* Search and Filter */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <h3>🔍 Find Problems</h3>
        
        {/* Search */}
        <div style={{ marginTop: '0.75rem' }}>
          <input
            type="text"
            placeholder="Search by name or symptom..."
            value={searchQuery}
            onChange={handleSearchChange}
            style={{ width: '100%', padding: '0.5rem' }}
          />
        </div>

        {/* Step 1: Category Filter */}
        <div style={{ marginTop: '0.75rem' }}>
          <label style={{ fontSize: '0.85rem', fontWeight: 500, display: 'block', marginBottom: '0.25rem' }}>
            Step 1: Choose Category (Optional)
          </label>
          <select
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value)
              setSelectedPlant('') // Reset plant when category changes
              setSelectedProblem(null)
            }}
            style={{ width: '100%', padding: '0.5rem' }}
          >
            <option value="">-- All Categories --</option>
            {Object.keys(plantsByCategory).sort().map(category => (
              <option key={category} value={category}>
                {getCategoryIcon(category)} {getCategoryLabel(category)} ({plantsByCategory[category].length} plants)
              </option>
            ))}
          </select>
        </div>

        {/* Step 2: Plant Filter */}
        <div style={{ marginTop: '0.75rem' }}>
          <label style={{ fontSize: '0.85rem', fontWeight: 500, display: 'block', marginBottom: '0.25rem' }}>
            Step 2: Choose Plant (Optional)
          </label>
          <select
            key={`plant-select-${selectedCategory}`} // Force remount when category changes
            value={selectedPlant}
            onChange={handlePlantChange}
            style={{ width: '100%', padding: '0.5rem' }}
            disabled={!selectedCategory}
          >
            <option value="">{selectedCategory ? '-- Select Plant --' : '-- Select a category first --'}</option>
            {selectedCategory && plantsByCategory[selectedCategory]?.map(plant => (
              <option key={plant.id} value={plant.id}>
                {plant.name}
              </option>
            ))}
          </select>
        </div>

        {/* Type Filter */}
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
          <button
            className="btn"
            style={{
              background: filterType === 'all' ? '#2d5a27' : 'white',
              color: filterType === 'all' ? 'white' : '#333',
              flex: 1
            }}
            onClick={() => handleFilterChange('all')}
          >
            All ({allProblems.length})
          </button>
          <button
            className="btn"
            style={{
              background: filterType === 'pest' ? '#2d5a27' : 'white',
              color: filterType === 'pest' ? 'white' : '#333',
              flex: 1
            }}
            onClick={() => handleFilterChange('pest')}
          >
            🐛 Pests ({pestCount})
          </button>
          <button
            className="btn"
            style={{
              background: filterType === 'disease' ? '#2d5a27' : 'white',
              color: filterType === 'disease' ? 'white' : '#333',
              flex: 1
            }}
            onClick={() => handleFilterChange('disease')}
          >
            🦠 Diseases ({diseaseCount})
          </button>
        </div>

        {/* Results count */}
        <p style={{ marginTop: '0.75rem', marginBottom: 0, color: '#666', fontSize: '0.85rem' }}>
          Showing {filteredProblems.length} problems
          {selectedCategory && ` in ${getCategoryLabel(selectedCategory)}`}
          {selectedPlant && ` for ${plants.find(p => p.id === selectedPlant)?.name || selectedPlant}`}
        </p>
      </div>

      {/* Problem List */}
      {selectedProblem ? (
        /* Detail View */
        <div className="card">
          <button 
            className="btn btn-secondary" 
            onClick={() => setSelectedProblem(null)}
            style={{ marginBottom: '1rem' }}
          >
            ← Back to List
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '2rem' }}>{getTypeIcon(selectedProblem.type)}</span>
            <h2 style={{ margin: 0 }}>{selectedProblem.name}</h2>
            <span 
              style={{ 
                padding: '0.25rem 0.5rem',
                background: getSeverityColor(selectedProblem.severity),
                color: 'white',
                borderRadius: '4px',
                fontSize: '0.75rem',
                textTransform: 'uppercase'
              }}
            >
              {selectedProblem.severity}
            </span>
          </div>

          {/* Affected Plants */}
          <div style={{ marginTop: '1rem' }}>
            <h4>🌱 Affected Plants</h4>
            {(() => {
              const affectedPlants = getAffectedPlants(selectedProblem)
              return affectedPlants.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                  {affectedPlants.map(plant => (
                    <span key={plant.id} style={{ background: '#e8f5e9', padding: '0.25rem 0.75rem', borderRadius: '12px', fontSize: '0.85rem' }}>
                      {getCategoryIcon(plant.category)} {plant.name}
                    </span>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#666', fontStyle: 'italic' }}>General garden pest/disease - affects many plants</p>
              )
            })()}
          </div>

          {/* Symptoms */}
          <div style={{ marginTop: '1.5rem' }}>
            <h4>📋 Symptoms</h4>
            <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
              {selectedProblem.symptoms.map((symptom, idx) => (
                <li key={idx} style={{ marginBottom: '0.5rem' }}>{symptom}</li>
              ))}
            </ul>
          </div>

          {/* Causes */}
          {selectedProblem.causes && (
            <div style={{ marginTop: '1.5rem' }}>
              <h4>🔍 Causes</h4>
              <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                {selectedProblem.causes.map((cause, idx) => (
                  <li key={idx} style={{ marginBottom: '0.5rem' }}>{cause}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Prevention */}
          <div style={{ marginTop: '1.5rem' }}>
            <h4>🛡️ Prevention</h4>
            <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
              {selectedProblem.prevention.map((tip, idx) => (
                <li key={idx} style={{ marginBottom: '0.5rem' }}>{tip}</li>
              ))}
            </ul>
          </div>

          {/* Treatments */}
          <div style={{ marginTop: '1.5rem' }}>
            <h4>💊 Treatments</h4>
            
            {/* Treatment Type Toggles */}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', marginBottom: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
                <input 
                  type="checkbox" 
                  checked={showNatural} 
                  onChange={(e) => setShowNatural(e.target.checked)}
                />
                🌿 Natural/Organic
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
                <input 
                  type="checkbox" 
                  checked={showChemical} 
                  onChange={(e) => setShowChemical(e.target.checked)}
                />
                ⚗️ Chemical
              </label>
            </div>

            {/* Natural Treatments */}
            {showNatural && selectedProblem.treatments.natural.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <h5 style={{ color: '#2d5a27' }}>🌿 Natural & Organic Solutions</h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.75rem' }}>
                  {selectedProblem.treatments.natural.map((treatment, idx) => (
                    <div key={idx} style={{ padding: '1rem', background: '#e8f5e9', borderRadius: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <h6 style={{ margin: 0, fontSize: '1rem' }}>{treatment.name}</h6>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <span style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', background: 'white', borderRadius: '4px' }}>
                            {treatment.effectiveness}
                          </span>
                          <span style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', background: 'white', borderRadius: '4px' }}>
                            {treatment.cost}
                          </span>
                        </div>
                      </div>
                      <p style={{ margin: '0.5rem 0', fontSize: '0.9rem' }}>{treatment.description}</p>
                      <div style={{ fontSize: '0.85rem', background: 'white', padding: '0.75rem', borderRadius: '4px', marginTop: '0.5rem' }}>
                        <strong>How to apply:</strong><br />
                        {treatment.instructions}
                        {treatment.frequency && (
                          <><br /><strong>Frequency:</strong> {treatment.frequency}</>
                        )}
                      </div>
                      {treatment.safetyNotes && (
                        <div style={{ fontSize: '0.8rem', color: '#d9534f', marginTop: '0.5rem' }}>
                          ⚠️ <strong>Safety:</strong> {treatment.safetyNotes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Chemical Treatments */}
            {showChemical && selectedProblem.treatments.chemical.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <h5 style={{ color: '#f44336' }}>⚗️ Chemical Solutions</h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.75rem' }}>
                  {selectedProblem.treatments.chemical.map((treatment, idx) => (
                    <div key={idx} style={{ padding: '1rem', background: '#ffebee', borderRadius: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <h6 style={{ margin: 0, fontSize: '1rem' }}>{treatment.name}</h6>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <span style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', background: 'white', borderRadius: '4px' }}>
                            {treatment.effectiveness}
                          </span>
                          <span style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', background: 'white', borderRadius: '4px' }}>
                            {treatment.cost}
                          </span>
                        </div>
                      </div>
                      <p style={{ margin: '0.5rem 0', fontSize: '0.9rem' }}>{treatment.description}</p>
                      <div style={{ fontSize: '0.85rem', background: 'white', padding: '0.75rem', borderRadius: '4px', marginTop: '0.5rem' }}>
                        <strong>How to apply:</strong><br />
                        {treatment.instructions}
                        {treatment.frequency && (
                          <><br /><strong>Frequency:</strong> {treatment.frequency}</>
                        )}
                      </div>
                      {treatment.safetyNotes && (
                        <div style={{ fontSize: '0.8rem', color: '#d9534f', marginTop: '0.5rem' }}>
                          ⚠️ <strong>Safety:</strong> {treatment.safetyNotes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* List View - KEY FIX: Use key to force re-render */
        <div key={`list-${selectedPlant}-${filterType}-${searchQuery}`} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filteredProblems.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
              <p>No problems found matching your criteria.</p>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '1rem' }}>
                {selectedPlant && (
                  <button className="btn btn-secondary" onClick={() => setSelectedPlant('')}>
                    Clear Plant Filter
                  </button>
                )}
                {searchQuery && (
                  <button className="btn btn-secondary" onClick={() => setSearchQuery('')}>
                    Clear Search
                  </button>
                )}
                {filterType !== 'all' && (
                  <button className="btn btn-secondary" onClick={() => setFilterType('all')}>
                    Show All Types
                  </button>
                )}
              </div>
            </div>
          ) : (
            filteredProblems.map(problem => (
              <div 
                key={problem.id}
                className="card"
                style={{ 
                  cursor: 'pointer',
                  borderLeft: `4px solid ${getSeverityColor(problem.severity)}`
                }}
                onClick={() => setSelectedProblem(problem)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '1.5rem' }}>{getTypeIcon(problem.type)}</span>
                      <h4 style={{ margin: 0 }}>{problem.name}</h4>
                      <span style={{ fontSize: '0.75rem', color: '#666', marginLeft: '0.5rem' }}>
                        ({problem.type})
                      </span>
                    </div>
                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: '#666' }}>
                      {problem.symptoms[0].substring(0, 80)}...
                    </p>
                    {/* Show affected plants count */}
                    {(() => {
                      const affectedCount = getAffectedPlants(problem).length
                      return affectedCount > 0 ? (
                        <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#2d5a27' }}>
                          Affects {affectedCount} plant{affectedCount !== 1 ? 's' : ''}
                        </p>
                      ) : null
                    })()}
                  </div>
                  <span style={{ 
                    fontSize: '0.75rem', 
                    padding: '0.25rem 0.5rem',
                    background: getSeverityColor(problem.severity),
                    color: 'white',
                    borderRadius: '4px'
                  }}>
                    {problem.severity}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}

export default DiseasePestGuide
